import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Avatar,
    Paper,
} from "@mui/material";
import { Share, Favorite, Visibility } from "@mui/icons-material";
import StatCardList from "./components/StatCardList";
import { useDispatch, useSelector } from "react-redux";
import { setLoading, unsetLoading } from "../../../store/loading/loading.slice";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import { checkPermission, formatNumber } from "../../../utils/app.utils";
import ContainerLayout from "../../../components/container-layout/ContainerLayout";
import StateComponents from "../../../components/state-component/StateComponents";
import StatisticsPieChart from "./components/PieChart";
import BarStatisticsChart from "./components/BarChart";
import StatCardSummary from "./components/StatCardSummary";
import {
    Group as GroupIcon,
    Article as ArticleIcon,
    // Newspaper as NewspaperIcon,
    LibraryBooks as LibraryBooksIcon,
} from "@mui/icons-material";
import LineChart from "./components/LineChart";
import { saveAs } from "file-saver";
import { alertError } from "../../../components/alert/swal";
import StatStaticList from "./components/StatStaticList";
import moment from "moment";
import { DashboardCmsApi } from "../../../api/dashboard-cms/dashboard_cms.api";
import { RootState } from "../../../store/store";

interface ActivityItem {
    id: number;
    type: string;
    count: string;
    label: string;
    color: string;
    bgColor: string;
}

const Dashboard: React.FC = () => {
    const dispatch = useDispatch();

    const today = moment().format("YYYY-MM-DD");
    const sevenDaysAgo = moment().subtract(7, 'day').format("YYYY-MM-DD")

    const [data, setData] = useState<any>({});
    const [inputValueSearch, setInputValueSearch] = useState<{ start_date: string, end_date: string }>({
        start_date: sevenDaysAgo,
        end_date: today
    });

    const profileReducer = useSelector((state: RootState) => state.profile.profile);
    const permission: any[] = profileReducer?.permission || [];
    const checkPermit = checkPermission('MANAGE_WEB_SITE.DASHBOARD', permission);

    const activities: ActivityItem[] = [
        {
            id: 1,
            type: "team",
            count: formatNumber(data?.statistics_fliter?.count_mem ?? 0),
            label: "จำนวนสมาชิก",
            color: "#FF947A",
            bgColor: "#FFF4DE",
        },
        {
            id: 2,
            type: "like",
            count: formatNumber(data?.statistics_fliter?.count_like ?? 0),
            label: "จำนวนการถูกใจ",
            color: "#FA5A7D",
            bgColor: "#FFE2E5",
        },
        // {
        //     id: 3,
        //     type: "edit",
        //     count: formatNumber(data?.statistics_fliter?.count_comment ?? 0),
        //     label: "ความคิดเห็น",
        //     color: "#3CC7DF",
        //     bgColor: "#E3FBFF",
        // },
        {
            id: 4,
            type: "view",
            count: formatNumber(data?.statistics_fliter?.count_view ?? 0),
            label: "ยอดการเข้าชม",
            color: "#BF83FF",
            bgColor: "#F3E8FF",
        },
        {
            id: 5,
            type: "share",
            count: formatNumber(data?.statistics_fliter?.count_share ?? 0),
            label: "ยอดการแชร์",
            color: "#49AF47",
            bgColor: "#DCFCE7",
        },
    ];

    const activities2: ActivityItem[] = [
        {
            id: 1,
            type: "team",
            count: formatNumber(data?.statistics_current?.count_mem ?? 0),
            label: "จำนวนสมาชิก",
            color: "#FF947A",
            bgColor: "#FFF4DE",
        },
        {
            id: 2,
            type: "like",
            count: formatNumber(data?.statistics_current?.count_like ?? 0),
            label: "จำนวนการถูกใจ",
            color: "#FA5A7D",
            bgColor: "#FFE2E5",
        },
        // {
        //     id: 3,
        //     type: "edit",
        //     count: formatNumber(data?.statistics_current?.count_comment ?? 0),
        //     label: "ความคิดเห็น",
        //     color: "#3CC7DF",
        //     bgColor: "#E3FBFF",
        // },
        {
            id: 4,
            type: "view",
            count: formatNumber(data?.statistics_current?.count_view ?? 0),
            label: "ยอดการเข้าชม",
            color: "#BF83FF",
            bgColor: "#F3E8FF",
        },
        {
            id: 5,
            type: "share",
            count: formatNumber(data?.statistics_current?.count_share ?? 0),
            label: "ยอดการแชร์",
            color: "#49AF47",
            bgColor: "#DCFCE7",
        },
    ];

    const getActivityIcon = (type: string) => {
        switch (type) {
            case "like":
                return <Favorite />;
            case "share":
                return <Share />;
            case "view":
                return <Visibility />;
            case "team":
                return <Groups2OutlinedIcon />;
            case "comment":
                return <ForumOutlinedIcon />;
            default:
                return <Favorite />;
        }
    };

    // const formatDate = (date: string | Date) =>
    //   new Date(date).toISOString().split("T")[0];
    const formatDate = (date: string | Date): string => {
        const m = moment(date);
        return m.isValid() ? m.format("YYYY-MM-DD") : "";
    };

    const handleGetData = async () => {
        dispatch(setLoading());

        await DashboardCmsApi
            .findAll({
                start_date: inputValueSearch.start_date
                    ? formatDate(inputValueSearch.start_date)
                    : "",
                end_date: inputValueSearch.start_date
                    ? formatDate(inputValueSearch.end_date)
                    : "",
            })
            .then((res) => {
                if (res.status < 300) {
                    const resData = res?.data?.data || {};
                    setData(resData);
                }
            })
            .catch((err) => {
                console.log(err);
                dispatch(unsetLoading());
            })
            .finally(() => {
                dispatch(unsetLoading());
            });
    };

    useEffect(() => {
        handleGetData();
    }, [inputValueSearch]);

    const handleDownloadFile = async () => {
        try {
            dispatch(setLoading());

            const res = await DashboardCmsApi.report({
                start_date: inputValueSearch.start_date
                    ? formatDate(inputValueSearch.start_date)
                    : "",
                end_date: inputValueSearch.start_date
                    ? formatDate(inputValueSearch.end_date)
                    : "",
            });

            if (res.status < 300) {
                const data = res.data;

                // Get content type and disposition headers
                const contentType =
                    res.headers["content-type"] || "application/octet-stream";
                const contentDisposition = res.headers["content-disposition"] || "";

                // Extract filename from content-disposition
                let filename = `dashboard-${new Date().getTime()}.xlsx`;
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match && match[1]) {
                    filename = decodeURIComponent(match[1]); // Handles %20 and other encoded characters
                }

                // Create and download the file
                const blob = new Blob([data], { type: contentType });
                saveAs(blob, filename);
            }
        } catch (error) {
            console.error(error);
            alertError("ดาวน์โหลด ไม่สำเร็จ");
        } finally {
            dispatch(unsetLoading());
        }
    };

    return (
        <ContainerLayout
            title={"Dashboard"}
            topRightContentClassName="flex justify-end items-center"
            navigateContent={<></>}
            buttonContent={
                <>
                    <StateComponents
                        otherProps={{ spacing: 2 }}
                        typeProps={[
                            {
                                seq: 2,
                                type: "DATE",
                                gridProps: {
                                    xs: 12,
                                    md: 4,
                                },
                                sx: {
                                    marginTop: "28px",
                                },
                                fieldProps: {
                                    onChange: (date) =>
                                        setInputValueSearch({
                                            ...inputValueSearch,
                                            start_date: date,
                                        }),
                                    value: inputValueSearch.start_date,
                                    placeholder: "วันที่เริ่มต้น",
                                },
                            },
                            {
                                seq: 3,
                                type: "DATE",
                                gridProps: {
                                    xs: 12,
                                    md: 4,
                                },
                                sx: {
                                    marginTop: "28px",
                                },
                                fieldProps: {
                                    onChange: (date) =>
                                        setInputValueSearch({
                                            ...inputValueSearch,
                                            end_date: date,
                                        }),
                                    value: inputValueSearch.end_date,
                                    placeholder: "วันที่สิ้นสุด",
                                },
                            },
                            {
                                seq: 5,
                                type: "BUTTON",
                                gridProps: {
                                    xs: 12,
                                    md: 4,
                                },
                                fieldProps: {
                                    justifyContent: "flex-end",
                                    buttonData: [
                                        {
                                            seq: 1,
                                            fullWidth: true,
                                            buttonProps: {
                                                onClick: () => {
                                                    handleDownloadFile();
                                                },
                                            },
                                            hidden: !checkPermit.isView,
                                            key: "IMPORT_EXCEL",
                                            label: "ดาวน์โหลด Excel",
                                            type: "CONFIRM",
                                        },
                                    ],
                                },
                            },
                        ]}
                    />
                </>
            }
            content={
                <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        {/* Activity Overview */}
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    height: "100%",
                                }}
                            >
                                <Box mb={3}>
                                    <Typography variant="h6" fontWeight="bold">
                                        สถิติตามข้อมูลการกรอง
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold">
                                        วันที่{" "}
                                        {new Date(inputValueSearch.start_date).toLocaleDateString(
                                            "th-TH",
                                            {
                                                year: "2-digit",
                                                month: "2-digit",
                                                day: "2-digit",
                                            }
                                        )}{" "}
                                        -{" "}
                                        {new Date(inputValueSearch.end_date).toLocaleDateString(
                                            "th-TH",
                                            {
                                                year: "2-digit",
                                                month: "2-digit",
                                                day: "2-digit",
                                            }
                                        )}
                                    </Typography>
                                </Box>

                                <Grid container spacing={2}>
                                    {activities.map((activity) => (
                                        <Grid item xs={6} lg={2.4} key={activity.id}>
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    bgcolor: activity.bgColor,
                                                    borderRadius: 2,
                                                    transition: "all 0.3s ease",
                                                    "&:hover": {
                                                        transform: "scale(1.02)",
                                                    },
                                                    minHeight: "135px",
                                                }}
                                            >
                                                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1}
                                                        mb={1}
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: activity.color,
                                                                width: 32,
                                                                height: 32,
                                                            }}
                                                        >
                                                            {getActivityIcon(activity.type)}
                                                        </Avatar>
                                                    </Box>
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight="bold"
                                                        sx={{
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        {activity.count}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {activity.label}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    height: "100%",
                                }}
                            >
                                <Box mb={3}>
                                    <Typography variant="h6" fontWeight="bold">
                                        สถิติ ณ ปัจจุบัน
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold">
                                        ณ วันที่{" "}
                                        {new Date(moment().subtract(1, 'day').format("YYYY-MM-DD:HH:mm")).toLocaleDateString("th-TH", {
                                            year: "2-digit",
                                            month: "2-digit",
                                            day: "2-digit",
                                        })}
                                    </Typography>
                                </Box>

                                <Grid container spacing={2}>
                                    {activities2.map((activity) => (
                                        <Grid item xs={6} lg={2.4} key={activity.id}>
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    bgcolor: activity.bgColor,
                                                    borderRadius: 2,
                                                    transition: "all 0.3s ease",
                                                    "&:hover": {
                                                        transform: "scale(1.02)",
                                                    },
                                                    minHeight: "135px",
                                                }}
                                            >
                                                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1}
                                                        mb={1}
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: activity.color,
                                                                width: 32,
                                                                height: 32,
                                                            }}
                                                        >
                                                            {getActivityIcon(activity.type)}
                                                        </Avatar>
                                                    </Box>
                                                    <Typography variant="h6" fontWeight="bold">
                                                        {activity.count}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: "12px",
                                                        }}
                                                        color="text.secondary"
                                                    >
                                                        {activity.label}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6} lg={4}>
                            <StatisticsPieChart
                                title="สถิติการเปลี่ยนแปลงต่างๆ"
                                data={data?.menu ?? []}
                                type="statistics_filter"
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={4}>
                            <BarStatisticsChart
                                title="สถิติการเข้าชม ข่าวประชาสัมพันธ์ / ปฏิทินกิจกรรม / Admission"
                                data={data?.view_news ?? []}
                                type="count"
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={2}>
                            <StatisticsPieChart
                                title="จำนวนการถูกใจรวม"
                                data={data?.sum_like ?? []}
                                type="sum_like"
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={2}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <StatCardSummary
                                        title="จำนวนสมาชิกสมัครเพิ่ม"
                                        amount={data?.increase?.count_mem ?? 0}
                                        icon={GroupIcon}
                                        color="coral"
                                        type="จำนวนสมาชิกสมัครเพิ่ม"
                                        unit="ราย"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <StatCardSummary
                                        title="จำนวนข่าวประชาสัมพันธ์"
                                        amount={data?.increase?.count_news ?? 0}
                                        icon={ArticleIcon}
                                        color="purple"
                                        type="จำนวนข่าวรวม"
                                        unit="รายการ"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <StatCardSummary
                                        title="จำนวนปฏิทินกิจกรรม"
                                        amount={data?.increase?.count_act ?? 0}
                                        icon={LibraryBooksIcon}
                                        color="green"
                                        type="จำนวนบทความรวม"
                                        unit="รายการ"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} md={12} lg={4}>
                            <StatCardList
                                type="like"
                                cardTitle="สถิติการถูกใจข่าว สูงสุด 5 อันดับแรก"
                                data={
                                    data?.top_news_like &&
                                    data?.top_news_like?.map((item: any) => {
                                        return {
                                            ...item,
                                            title: item.name,
                                            icon: <Favorite sx={{ fontSize: 12, color: "#fff" }} />,
                                            value: item.count_num,
                                        };
                                    })
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={12} lg={4}>
                            <StatCardList
                                type="share"
                                cardTitle="สถิติข่าวที่มีการแชร์ สูงสุด 5 อันดับแรก"
                                data={
                                    data?.top_news_share &&
                                    data?.top_news_share?.map((item: any) => {
                                        return {
                                            ...item,
                                            title: item.name,
                                            icon: <Share sx={{ fontSize: 12, color: "#fff" }} />,
                                            value: item.count_num,
                                        };
                                    })
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={12} lg={4}>
                            <StatCardList
                                type="view"
                                cardTitle="สถิติข่าวที่ถูกอ่านมากที่สุด 5 อันดับแรก"
                                data={
                                    data?.top_news_view &&
                                    data?.top_news_view?.map((item: any) => {
                                        return {
                                            ...item,
                                            title: item.name,
                                            icon: <Visibility sx={{ fontSize: 12, color: "#fff" }} />,
                                            value: item.count_num,
                                        };
                                    })
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={12} lg={12}>
                            <LineChart
                                data={data?.graph_share ?? []}
                                title="จำนวนการแชร์ (แยกตามประเภทโซเชียลมีเดีย)"
                                type="count"
                                platforms={["line", "facebook", "x"]}
                            />
                        </Grid>

                        <Grid item xs={12} md={12} lg={6}>
                            <StatStaticList
                                title="สถิติประเภทข่าวที่ได้รับความนิยม"
                                data={
                                    data?.popular_category
                                        ? data?.popular_category?.map((item: any) => {
                                            return {
                                                ...item,
                                                text: item.name,
                                            };
                                        })
                                        : []
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={12} lg={6}>
                            <StatStaticList
                                title="สถิติ TAG ที่ได้รับความนิยม"
                                data={
                                    data?.popular_tag
                                        ? data?.popular_tag?.map((item: any) => {
                                            return {
                                                ...item,
                                                text: item.name,
                                                value: item.name,
                                            };
                                        })
                                        : []
                                }
                            />
                        </Grid>
                    </Grid>
                </Box>
            }
        />
    );
};

export default Dashboard;
